import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetFTLConsolidatedDataQuery } from '@/redux/slices/pallet/palletApiSlice';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";

interface ConsolidatedViewProps {
  isOpen: boolean;
onClose: () => void;
  variant: string;
}

const FTLConsolidatedView: React.FC<ConsolidatedViewProps> = ({ isOpen, onClose, variant }) => {
  const [activeTab, setActiveTab] = useState('raw');
  const [activeStatistic, setActiveStatistic] = useState('average');
  const { projectId } = useSelector((state: RootState) => state.define);
  const { data: consolidatedData, isLoading, error, refetch } = useGetFTLConsolidatedDataQuery({ projectId, variant }, { refetchOnMountOrArgChange: true });
  console.log(consolidatedData,'consolidatedData')
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);
  console.log(consolidatedData, error)
  console.log(consolidatedData, 'consolidatedData.rawData')
  if (isLoading) return <div>Loading...</div>;
  // if (error) return <div>Error loading data</div>;
  if (error) return <div></div>;
  const renderRawDataTable = () => {
    // Helper function to determine if a measurement is within range
    const isWithinRange = (measurement: number, palletName: string, position: 'top' | 'middle' | 'bottom') => {
      // Find the pallet details in the statistics
      const palletDetail = consolidatedData.palletDetails.find(
        (p: any) => p.palletName === palletName
      );

      if (!palletDetail) return { isWithin: true, isAbove: false, isNA: false }; // Default if not found

      // For N/A values, return special case
      if (isNaN(measurement) || measurement === 0) {
        return { isWithin: false, isAbove: false, isBelow: false, isNA: true };
      }

      let calculatedTarget = 0;
      let maxTarget = 0;

      // Set target ranges based on position
      if (position === 'top') {
        calculatedTarget = Number(palletDetail.topCalculatedTarget);
        maxTarget = Number(palletDetail.topMax);
      } else if (position === 'middle') {
        calculatedTarget = Number(palletDetail.middleCalculatedTarget);
        maxTarget = Number(palletDetail.middleMax);
      } else if (position === 'bottom') {
        calculatedTarget = Number(palletDetail.bottomCalculatedTarget);
        maxTarget = Number(palletDetail.bottomMax);
      }

      const isAbove = measurement > maxTarget;
      const isBelow = measurement < calculatedTarget;
      const isWithin = !isAbove && !isBelow;

      return { isWithin, isAbove, isBelow, isNA: false };
    };

    // 1. Group raw data by palletId
    const groupedByPalletId = consolidatedData.rawData.reduce((groups: Record<string, any[]>, row: any) => {
      const palletId = row.palletId || 'unknown';
      if (!groups[palletId]) {
        groups[palletId] = [];
      }
      groups[palletId].push(row);
      return groups;
    }, {});

    // 2. Sort groups by palletCreationDate and then sort each group by lpnIndex
    const sortedGroups = Object.entries(groupedByPalletId)
      // Sort groups by palletCreationDate
      .sort(([, groupA], [, groupB]) => {
        const dateA = new Date(groupA[0]?.palletCreationDate || 0).getTime();
        const dateB = new Date(groupB[0]?.palletCreationDate || 0).getTime();
        return dateA - dateB;
      })
      .map(([, group]) => {
        // Sort each group by lpnIndex
        return group.sort((a, b) => {
          const indexA = parseInt(a.lpnIndex) || 0;
          const indexB = parseInt(b.lpnIndex) || 0;
          return indexA - indexB;
        });
      });

    // 3. Flatten the sorted groups back into a single array
    const sortedData = sortedGroups.flat();

    return (
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet No</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Pallet Name</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Top Measured (lbs)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Middle Measured (lbs)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Bottom Measured (lbs)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((row: any, index: number) => {
            // Determine if values are within range
            const topMeasured = row.topMeasured || "N/A";
            const middleMeasured = row.middleMeasured || "N/A";
            const bottomMeasured = row.bottomMeasured || "N/A";

            const topMeasurement = topMeasured === "N/A" ? NaN : parseFloat(topMeasured);
            const middleMeasurement = middleMeasured === "N/A" ? NaN : parseFloat(middleMeasured);
            const bottomMeasurement = bottomMeasured === "N/A" ? NaN : parseFloat(bottomMeasured);

            const topStatus = isWithinRange(topMeasurement, row.palletName, 'top');
            const middleStatus = isWithinRange(middleMeasurement, row.palletName, 'middle');
            const bottomStatus = isWithinRange(bottomMeasurement, row.palletName, 'bottom');

            return (
              <TableRow key={`${row.palletId}-${row.lpnIndex || index}`} className={"bg-gray-100"}>
                <TableCell className="border text-center border-gray-300 text-center p-2">{row.lpnIndex || index + 1}</TableCell>
                <TableCell className="border border-gray-300 text-center p-2">{row.palletName}</TableCell>
                <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
                <TableCell
                  className={cn(
                    "border border-gray-300 text-center p-2",
                    topStatus.isNA ? "" : (topStatus.isWithin ? "bg-states-success text-white" : "bg-bgRed text-white")
                  )}
                >
                  {row.topMeasured}
                  {!topStatus.isNA && !topStatus.isWithin && (
                    <span className="ml-2">
                      {topStatus.isAbove ? "↑" : "↓"}
                    </span>
                  )}
                </TableCell>
                <TableCell
                  className={cn(
                    "border border-gray-300 text-center p-2",
                    middleStatus.isNA ? "" : (middleStatus.isWithin ? "bg-states-success text-white" : "bg-bgRed text-white")
                  )}
                >
                  {row.middleMeasured}
                  {!middleStatus.isNA && !middleStatus.isWithin && (
                    <span className="ml-2">
                      {middleStatus.isAbove ? "↑" : "↓"}
                    </span>
                  )}
                </TableCell>
                <TableCell
                  className={cn(
                    "border border-gray-300 text-center p-2",
                    bottomStatus.isNA ? "" : (bottomStatus.isWithin ? "bg-states-success text-white" : "bg-bgRed text-white")
                  )}
                >
                  {row.bottomMeasured}
                  {!bottomStatus.isNA && !bottomStatus.isWithin && (
                    <span className="ml-2">
                      {bottomStatus.isAbove ? "↑" : "↓"}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };
  const renderStatisticsTable = () => (
    <div className='w-full overflow-auto'>
      <div className="space-y-6 overflow-x-auto">
      <Table className="min-w-full border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Weight</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Calculated FTL Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Plus By user</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Percentage</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Top (lbs)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Middle (lbs)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Bottom (lbs)</TableHead>
          </TableRow>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Calculated Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Calculated Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Calculated Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Target</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max Range</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.palletDetails.map((pallet: any, index: number) => (
            <TableRow key={index} className={"bg-gray-100"}>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.palletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.weight}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.calculatedFTLTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.plusByUser}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{pallet.percentage}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.topCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.topCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.topMax).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.middleCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.middleCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.middleMax).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.bottomCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.bottomCalculatedTarget).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Number(pallet.bottomMax).toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Table className="min-w-full border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold" rowSpan={2}>Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Top (lbs)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Middle (lbs)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold" colSpan={3}>Bottom (lbs)</TableHead>
          </TableRow>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Min</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">StDev</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Min</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">StDev</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Min</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Max</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">StDev</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.statistics.min.map((row: any, index: number) => (
            <TableRow key={index} className={"bg-gray-100"}>
              <TableCell className="border border-gray-300 text-center p-2">{row.palletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(row.top)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.max[index].top)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.stdev[index].top)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(row.middle)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.max[index].middle)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.stdev[index].middle)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(row.bottom)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.max[index].bottom)).toFixed(2)}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{Math.abs(parseFloat(consolidatedData.statistics.stdev[index].bottom)).toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Table className="min-w-full border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Row Labels</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Pallet Name</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Top Measured</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Middle Measured</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Count of Bottom Measured</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Data Fill Status</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">FTL Range</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Top Above Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Top Below Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Middle Above Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Middle Below Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Bottom Above Recommended Count</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2  font-semibold">Bottom Below Recommended Count</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.additionalStats.map((row: any, index: number) => (
            <TableRow key={index} className={"bg-gray-100"}>
              <TableCell className="border border-gray-300 text-center p-2">{row.rowLabel}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfPalletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfTopMeasured}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfMiddleMeasured}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.countOfBottomMeasured}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.dataFillStatus}</TableCell>
              <TableCell
                className={cn(
                  "border border-gray-300 text-center p-2",
                  {
                    "text-white bg-bgRed": row.ftlRange === 'Fail',
                    "text-white bg-states-success": row.ftlRange === 'Pass' && row.dataFillStatus !== 'Incomplete',
                    "text-white bg-[#F79E19]": row.ftlRange === 'Pass' && row.dataFillStatus === 'Incomplete',
                  }
                )}
              >
                {row.ftlRange === 'Pass' && row.dataFillStatus === 'Incomplete' ? (
                  "Data Missing"
                ) : (
                  row.ftlRange
                )}
              </TableCell>
              <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.topAboveRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.topAboveRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.topBelowRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.topBelowRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.middleAboveRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.middleAboveRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.middleBelowRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.middleBelowRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.bottomAboveRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.bottomAboveRecommendedCount}</TableCell>
              <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.bottomBelowRecommendedCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.bottomBelowRecommendedCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </div>
  );
  const renderGraph = () => {
    const graphData = consolidatedData.statistics[activeStatistic].map((item: any) => ({
      name: item.palletName,
      Top: Math.abs(parseFloat(item.top)),
      Middle: Math.abs(parseFloat(item.middle)),
      Bottom: Math.abs(parseFloat(item.bottom)),
    }));

    const colors = {
      Top: "#004899",
      Middle: "#82ca9d",
      Bottom: "#ffc658"
    };

    return (
      <div className="space-y-4">
        <div className="flex space-x-2">
          {['average', 'max', 'min', 'stdev'].map((stat) => (
            <Button
              key={stat}
              onClick={() => setActiveStatistic(stat)}
              className={cn(
                "px-4 justify-start hover:bg-blue hover:text-white",
                activeStatistic === stat ? "bg-blue text-white" : "bg-[#FAFAFA] text-gray-700"
              )}
            >
              {stat.charAt(0).toUpperCase() + stat.slice(1)}
            </Button>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar barSize={20} dataKey="Top" fill={colors.Top} />
            <Bar barSize={20} dataKey="Middle" fill={colors.Middle} />
            <Bar barSize={20} dataKey="Bottom" fill={colors.Bottom} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className=" max-w-[96vw] max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>FTL Consolidated View {`(${variant === 'postShip' ? "Post Ship" : variant === 'preShip' ? "Pre Ship" : "Post Stack"})`}</DialogTitle>
        </DialogHeader>
        <div className="flex h-[calc(100vh-200px)] w-[92vw] ">
          <div className="w-48 pt-10 bg-bgBlue">
            <div className="">
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'raw' ? " bg-blue text-white" : " text-gray-700 bg-bgBlue"
                )}
                onClick={() => setActiveTab('raw')}
              >
                Raw Data
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'statistics' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('statistics')}
              >
                Statistics
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'graphs' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('graphs')}
              >
                Graphs
              </Button>
            </div>
          </div>
          <div className="w-full flex-1 p-6 overflow-auto max-h-[calc(90vh-100px)]">
            {activeTab === 'raw' && renderRawDataTable()}
            {activeTab === 'statistics' && renderStatisticsTable()}
            {activeTab === 'graphs' && renderGraph()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FTLConsolidatedView;
